
import { strict } from "assert";
import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {DateGroup} from './AnalyticsCommon'

const colors = [
    '#1f77b4', // Blue
    '#ff7f0e', // Orange
    '#2ca02c', // Green
    '#d62728', // Red
    '#9467bd', // Purple
    '#8c564b', // Brown
    '#e377c2', // Pink
    '#7f7f7f', // Gray
    '#bcbd22', // Olive Green
    '#17becf'  // Cyan
  ];

function dateToStr(date: Date) {
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
}

function getDatesInRange(startDate: Date, endDate: Date) {
    const dates = [];

    // Create Date objects from the input strings
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    const end = new Date(endDate);

    while (currentDate <= end) {
        dates.push(new Date(currentDate));  // pushing a new Date object to avoid modifying the same reference
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
}

function getMondaysInRange(startDate: Date, endDate: Date) {
    const mondays = [];

    // Create Date objects from the input strings
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    const end = new Date(endDate);

    // Find the next Monday from the starting date
    while (currentDate.getDay() !== 1) {
        currentDate.setDate(currentDate.getDate() - 1);
    }

    // Keep adding 7 days to get the next Monday
    while (currentDate <= end) {
        mondays.push(new Date(currentDate));  // pushing a new Date object to avoid modifying the same reference
        currentDate.setDate(currentDate.getDate() + 7);
    }

    return mondays;
}

function getFirstDaysOfMonthInRange(startDate: Date, endDate: Date) {
    const firstDays = [];

    // Create Date objects from the input strings
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);
    const end = new Date(endDate);

    // Set the date to the first day of the current month
    currentDate.setDate(1);

    while (currentDate <= end) {
        firstDays.push(new Date(currentDate));  // pushing a new Date object to avoid modifying the same reference
        currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return firstDays;
}

function generateDates(dateGroup: string, start: Date, end: Date) {
    switch (dateGroup) {
        case DateGroup.Daily: return getDatesInRange(start, end)
        case DateGroup.Weekly: return getMondaysInRange(start, end)
        case DateGroup.Monthly: return getFirstDaysOfMonthInRange(start, end)
        default: break;
    }
    throw `Invalid date dateGroup:[${dateGroup}]`
}

function prepareData(config: any, data: any, fillGaps: boolean | undefined) {
    let dates = generateDates(config.dateGroup, config.period[0], config.period[1])
    //console.log(`DATES: ${dates} - ${dates.map(d => dateToStr(d))}`)

    if (!data || data.length <= 0) {
        return dates.map(item => { return {date: dateToStr(item), data: 0} })
    }

    let tmpl = {...data[0]}
    delete tmpl['date']
    Object.keys(tmpl).forEach(key => {
        tmpl[key] = fillGaps ? 0 : null;
    });

    const daysArray = [];
    for (let i = 0; i < dates.length; i += 1) {
        const date = dates[i]
        if (data.length == 0) {
            daysArray.push({ ...tmpl, date: dateToStr(date)});
            continue
        }

        let dataDate = data[0].date
        if (i == dates.length - 1 || dates[i+1] > dataDate) {
            daysArray.push({...data[0], ...{date: dateToStr(dataDate)}});
            data.shift()
        } else {
            daysArray.push({ ...tmpl, date: dateToStr(date)});
        }
    }

    //console.log(`CHART: ${JSON.stringify(daysArray)}`)
    return daysArray
}

const AnalyticsChart: React.FC<{data: any, height?: number, fillGaps?: boolean}> = (props) => {
    const [chartData, setChartData] = React.useState<any>(null)

    React.useEffect(() => {
        if (props.data) {
            setChartData(prepareData(props.data.config, props.data.data, props.fillGaps))
        }

    }, [props.data]);

    return (
        <>
        <h5 style={{ textAlign: 'center' }}>{props.data && props.data.config ? props.data.config.title : ''}</h5>
        <ResponsiveContainer width="100%" height={ props.height ? props.height : 400}>
            <LineChart
                data={chartData}
                margin={{
                    top: 5, right: 30, left: 20, bottom: 5,
                }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false}/>
                <Tooltip />
                { chartData && chartData.length > 0 && Object.keys(chartData[0]).map((key, index) => {
                    if (key == 'date') {
                        return null
                    }
                    return (
                        <React.Fragment key={key}>
                            <YAxis yAxisId={key} hide/>
                            <Line type="linear" yAxisId={key} dataKey={key} stroke={colors[index % colors.length]} activeDot={{ r: 8 }}/>
                        </React.Fragment>
                    )
                })}
            </LineChart>
        </ResponsiveContainer>
        </>
    );
};

export default AnalyticsChart;